<template>
  <div class="dashboard-container p-4">
    <div class="bg-white p-5 rounded-lg shadow mb-4">
      <h1 class="text-2xl font-bold text-gray-800">
        Finance Dashboard
      </h1>
      <p class="text-gray-600">
        Manage invoices, purchase orders, and financial reports.
      </p>
    </div>
        
    <!-- Financial Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-blue-600 mb-2">
              £125,836
            </div>
            <div class="text-sm text-gray-600">
              Total Revenue (MTD)
            </div>
            <div class="mt-2 text-green-500 text-sm font-medium">
              ↑ 12.5%
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-orange-600 mb-2">
              £47,250
            </div>
            <div class="text-sm text-gray-600">
              Outstanding Invoices
            </div>
            <div class="mt-2 text-red-500 text-sm font-medium">
              ↑ 5.2%
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-green-600 mb-2">
              £35,785
            </div>
            <div class="text-sm text-gray-600">
              Paid This Month
            </div>
            <div class="mt-2 text-green-500 text-sm font-medium">
              ↑ 8.3%
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-purple-600 mb-2">
              £18,450
            </div>
            <div class="text-sm text-gray-600">
              Purchase Orders
            </div>
            <div class="mt-2 text-yellow-500 text-sm font-medium">
              ↓ 2.1%
            </div>
          </div>
        </template>
      </Card>
    </div>
        
    <!-- Quick Actions Buttons -->
    <div class="flex flex-wrap gap-2 mb-4">
      <Button
        label="New Invoice"
        icon="pi pi-file"
        severity="secondary"
      />
      <Button
        label="New Purchase Order"
        icon="pi pi-shopping-cart"
        severity="secondary"
      />
      <Button
        label="Generate Reports"
        icon="pi pi-chart-bar"
        severity="secondary"
      />
      <Button
        label="Approve Payments"
        icon="pi pi-check-circle"
        severity="secondary"
      />
    </div>
        
    <!-- Recent Transactions Table -->
    <div class="bg-white p-5 rounded-lg shadow mb-4">
      <h2 class="text-xl font-semibold mb-4">
        Recent Transactions
      </h2>
      <DataTable
        :value="recentTransactions"
        :rows="5"
        striped-rows
        responsive-layout="scroll"
      >
        <Column
          field="id"
          header="ID"
          style="width: 5%"
        />
        <Column
          field="date"
          header="Date"
          style="width: 15%"
        />
        <Column
          field="client"
          header="Client"
          style="width: 25%"
        />
        <Column
          field="amount"
          header="Amount"
          style="width: 15%"
        >
          <template #body="slotProps">
            <span
              class="font-semibold"
              :class="slotProps.data.type === 'incoming' ? 'text-green-600' : 'text-red-600'"
            >
              {{ slotProps.data.type === 'incoming' ? '+' : '-' }}{{ slotProps.data.amount }}
            </span>
          </template>
        </Column>
        <Column
          field="status"
          header="Status"
          style="width: 15%"
        >
          <template #body="slotProps">
            <Tag
              :value="slotProps.data.status"
              :severity="getStatusSeverity(slotProps.data.status)"
            />
          </template>
        </Column>
        <Column
          field="actions"
          header="Actions"
          style="width: 15%"
        >
          <template #body>
            <Button
              icon="pi pi-eye"
              text
              rounded
              aria-label="View"
            />
            <Button
              icon="pi pi-pencil"
              text
              rounded
              aria-label="Edit"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

// Sample recent transactions
const recentTransactions = ref([
    { 
        id: 'INV-1001', 
        date: '2023-05-15', 
        client: 'Tech Conference Ltd',
        amount: '£5,250',
        type: 'incoming',
        status: 'Paid'
    },
    { 
        id: 'PO-3021', 
        date: '2023-05-14', 
        client: 'Lighting Supplies Co.',
        amount: '£1,875',
        type: 'outgoing',
        status: 'Processing'
    },
    { 
        id: 'INV-998', 
        date: '2023-05-12', 
        client: 'City Events Agency',
        amount: '£3,750',
        type: 'incoming',
        status: 'Pending'
    },
    { 
        id: 'INV-995', 
        date: '2023-05-10', 
        client: 'Corporate Solutions Inc',
        amount: '£7,200',
        type: 'incoming',
        status: 'Overdue'
    },
    { 
        id: 'PO-3018', 
        date: '2023-05-08', 
        client: 'Audio Equipment Ltd',
        amount: '£2,340',
        type: 'outgoing',
        status: 'Complete'
    }
]);

// Function to determine status severity for tags
const getStatusSeverity = (status) => {
    switch (status.toLowerCase()) {
        case 'paid':
        case 'complete':
            return 'success';
        case 'pending':
        case 'processing':
            return 'info';
        case 'overdue':
            return 'danger';
        default:
            return 'warning';
    }
};
</script>

<style scoped>
.dashboard-container {
    background-color: #f5f7fa;
    min-height: 100vh;
}
</style> 